import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { Row, Col, Space, message, Dropdown, Upload } from 'antd';
import { ExportOutlined } from "@ant-design/icons";
import UserConfig from "@/config/user";
import { getOnOkFunction } from "@/lib/antdElementFunction";
import { getDetailsArray } from "@/lib/user";
import { dateFormat, getMoment, getCurrentTime } from "@/lib/dateTime";
import { exportExcel } from "@/lib/exlsDownload";
import { excelUploadProps } from "@/lib/uploadFunction";
import { getFileTitleObject, exportUserOptions, excelTitleUser } from "@/lib/humanResourceManagement";
import { ReModal, ReButton } from "@/components/refineElement";
import { ModalProfileTeacher } from "@/components/modal";
import { getUserAccountById } from "@/stores/user/action";
import { getLangList } from '@/lib/i18n';
import { hasPermission } from "@/lib/permissionUtil";
import { TeacherType } from './types';
import TeacherTable from './TeacherTable';

const { createUser, exportUserFile, importFacultyFile } = UserConfig;

type Props = {
  teacherArray: TeacherType[];
  getAllTeacherAccountFunction: Function;
}

function Teachers(props: Props) {
  const { teacherArray, getAllTeacherAccountFunction } = props;
  const [teacherInfo, setTeacherInfo] = useState({} as TeacherType);
  const [isModalProfileVisible, setIsModalProfileVisible] = useState(false);
  const [handleType, setHandleType] = useState('');
  const dispatch: any = useDispatch();
  const { userAccountId } = teacherInfo;
  const studentListT: any = getLangList("studentList");
  const organizationT: any = getLangList("organization");
  const profileInfoT: any = getLangList("profileInfo");
  const errorT: any = getLangList("error");
  const buttonT: any = getLangList("button");
  const errorCodeT: any = getLangList("errorCode");
  const fileTitleObject = getFileTitleObject('TEACHER_WORK', 'TEACHER_RESIGN');
  const getUserAccountByIdFunction = (userAccountId: number) => () => {
    const params = { userAccountId };
    dispatch(getUserAccountById(params));
  }
  const getUserInfo = handleType === 'updateProfileInfo' ? getUserAccountByIdFunction(userAccountId) : () => { };
  const okUserFunction = {
    createUser: createUserFunction,
  };

  function toggleProfileModal(handleType: string = '') {
    setIsModalProfileVisible(!isModalProfileVisible);
    setHandleType(handleType);
  };

  function clickUserCreate() {
    setTeacherInfo({} as TeacherType);
    toggleProfileModal('createUser');
  };

  function clickUserExport(e: any) {
    const { key } = e;
    const params = { personType: 2, isNeedLeave: key };

    exportUserFile(params).then((res: any) => {
      const { success, value } = res;
      if (!success) {
        return message.error(errorT.failed);
      }
      const containRange = key === 'true' ? fileTitleObject.workAndResign : fileTitleObject.work;
      const fileTitle = `${organizationT.TEACHER}(${containRange})_${getCurrentTime().format(dateFormat)}`;
      const rowData = value.map((item: any) => [
        item.username,
        item.roleName,
        item.birthday ? getMoment(item.birthday).format(dateFormat) : '',
        item.nameCn,
        item.nameEn,
        item.gender,
        item.identityNumber,
        item.residenceAddress,
        item.mailingAddress,
        item.school,
        item.grade,
        item.classroom,
        item.seatNumber,
        item.allergy,
        item.cramClassName,
        item.cramClassSeatNum,
        item.isLunch === 1 ? 'TRUE' : 'FALSE',
        item.isSnack === 1 ? 'TRUE' : 'FALSE',
        item.extension,
        item.departmentName,
        item.dueDate ? getMoment(item.dueDate).format(dateFormat) : '',
        item.businessEmail,
        item.personalEmail,
        item.accessCardNumber,
        item.cellPhoneNumber,
        item.year,
        item.semesterName,
      ]);
      exportExcel({
        data: [excelTitleUser, ...rowData],
        workbookName: fileTitle,
      });
    });
  };

  function clickImportFacultyFile(e: any) {
    const { file, onSuccess, onError } = e;
    const params = { fileFormat: { file } };
    const isTooLarge = file.size / 1024 / 1024 > 50;

    if (isTooLarge) {
      message.error(errorT.IMAGE_TOO_LARGE);
      return onError();
    }

    importFacultyFile(params).then((res: any) => {
      if (!res.success) {
        message.error(errorCodeT[res.errorCode] ?? errorT.failed);
        return onError();
      }
      onSuccess();
      message.success(errorT.success);
      getAllTeacherAccountFunction();
    });
  };

  function createUserFunction(values: any) {
    const {
      username,
      password,
      nameCn,
      nameEn,
      departmentId,
      roleId,
      extension,
      cellPhoneNumber,
      businessEmail,
      personalEmail,
      accessCardNumber,
      agentDepartmentId,
      agentUserAccountId,
      dueDate,
      terminationDate,
      birthday,
      gender,
      identityNumber,
      idCardImg,
      qrCode,
    } = values;
    const detailsArray = getDetailsArray(values);
    const userInfoData = {
      username,
      password,
      nameCn,
      nameEn,
      departmentId,
      roleId,
      extension,
      cellPhoneNumber,
      businessEmail,
      personalEmail,
      accessCardNumber,
      agentDepartmentId: agentDepartmentId ? agentDepartmentId : null,
      agentUserAccountId: agentUserAccountId ? agentUserAccountId : null,
      dueDate: dueDate?.format(dateFormat) ?? '',
      terminationDate: terminationDate?.format(dateFormat) ?? '',
      birthday: birthday?.format(dateFormat) ?? '',
      gender,
      identityNumber,
      details: detailsArray,
    };
    const params = { stringFormat: { userInfo: userInfoData }, fileFormat: { idCardImg, qrCode } };

    createUser(params).then((res: any) => {
      if (!res.success) {
        return message.error(errorCodeT[res.errorCode] ?? errorT.failed);
      }
      message.success(errorT.success);
      getAllTeacherAccountFunction();
      toggleProfileModal();
    });
  };

  return (
    <>
      <Row gutter={[0, 12]} justify="end">
        <Col>
          <Space>
            {hasPermission('HR_MANAGEMENT_TEACHER_NEW') &&
              <ReButton iconName='plus' onClick={clickUserCreate}>
                {`${buttonT.NEW}${studentListT.TEACHER}`}
              </ReButton>
            }

            {hasPermission('HR_MANAGEMENT_TEACHER_NEW') &&
              <Upload {...excelUploadProps} customRequest={clickImportFacultyFile}>
                <ReButton iconName='import'>
                  {buttonT.IMPORT}
                </ReButton>
              </Upload>
            }

            <Dropdown.Button
              menu={{
                items: exportUserOptions(fileTitleObject),
                onClick: clickUserExport,
              }}
            >
              <ExportOutlined />
              {buttonT.EXPORT}
            </Dropdown.Button>
          </Space>
        </Col>

        <Col span={24}>
          <TeacherTable
            teacherArray={teacherArray}
            fetchTeacherFunction={getAllTeacherAccountFunction}
          />
        </Col>
      </Row>

      {isModalProfileVisible &&
        <ReModal
          title={profileInfoT.PROFILE_INFO}
          onCancel={toggleProfileModal}
          visible={isModalProfileVisible}
          onOk={(data: any) => getOnOkFunction(handleType, data, okUserFunction)}
          CollectedForm={ModalProfileTeacher}
          formProps={{
            getUserInfo,
            handleType,
          }}
          width={'700px'}
        />
      }
    </>
  )
}

export default Teachers;